import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import { deriveViewerState } from './deriveViewerState';

async function effectiveTaskTagIds(task, dispatch, rootState) {
  const directTagIds = Array.isArray(task?.forgeTagsIds) ? task.forgeTagsIds : [];
  if (!task?.id) return [...new Set(directTagIds)];
  try {
    const entries = await dispatch('TASK/GET_TASKS_ISOLATION_BY_IDS', {
      taskIds: [task.id],
      assetId: rootState.assetId,
    }, { root: true });
    const entry = Array.isArray(entries)
      ? entries.find((candidate) => candidate?.taskId === task.id)
      : null;
    const scopedTagIds = Array.isArray(entry?.effectiveTagIds) ? entry.effectiveTagIds : [];
    return [...new Set(scopedTagIds.length > 0 ? scopedTagIds : directTagIds)];
  } catch (err) {
    debugError('[probeTaskState] task isolation lookup failed; using direct tags', err);
    return [...new Set(directTagIds)];
  }
}

/**
 * Probe a task's CURRENT state for the share-intent self-healing logic.
 * Both taskBoardIntent.play and taskExpandIntent.play call this to decide
 * whether to delegate to the other intent. Encapsulates the single source
 * of truth for "what is this task right now, and can it be visualized at
 * this facility?"
 *
 * Result shape:
 *   - ok: true if probe completed (regardless of result), false on hard
 *     network failure (caller can surface a friendly toast).
 *   - task: the AppTask entity (or null if not found / probe failed)
 *   - fullTags: the resolved tag entities (or null if no tags / fetch failed)
 *   - viewerState: deriveViewerState's output (or null if no tags)
 *   - effectiveTagIds: direct or Task-isolation context subjects
 *   - hasTags: true iff the isolation contract resolves at least one subject
 *   - canVisualize: true iff a meaningful viewer can be opened at the
 *     CURRENT facility for this task. The boolean partition that bounds
 *     the share-intent delegation chain depends on this being correct.
 *
 * Failure-mode design: every error path produces canVisualize=false so the
 * caller falls into the "task-board" safe end-state. The probe NEVER throws.
 *
 * Cost: one task read plus one isolation-contract read, followed by one tag
 * hydration request when effective subjects exist.
 *
 * @param {string} taskId
 * @param {{ dispatch: Function, rootState: object }} ctx
 * @returns {Promise<{
 *   ok: boolean,
 *   task: object | null,
 *   fullTags: object[] | null,
 *   viewerState: object | null,
 *   effectiveTagIds: string[],
 *   hasTags: boolean,
 *   canVisualize: boolean,
 * }>}
 */
export async function probeTaskState(taskId, { dispatch, rootState }) {
  const empty = {
    ok: false,
    task: null,
    fullTags: null,
    viewerState: null,
    effectiveTagIds: [],
    hasTags: false,
    canVisualize: false,
  };
  if (!taskId) return empty;

  // A hard task-read failure returns ok=false so the caller
  // can choose to throw a friendly toast (task-expand path does) or fall
  // through to task-board (task-board path does).
  let task;
  try {
    const res = await dispatch('TASK/GET_BY_ID', taskId, { root: true });
    task = res?.data ?? res;
  } catch (err) {
    debugError('[probeTaskState] TASK/GET_BY_ID failed', err);
    return empty;
  }
  if (!task) {
    return { ...empty, ok: true };
  }

  // TaskMonitoringScopes and anomaly component contexts are not duplicated
  // into forgeTagsIds, so the isolation contract owns the effective subjects.
  const effectiveTagIds = await effectiveTaskTagIds(task, dispatch, rootState);
  const hasTags = effectiveTagIds.length > 0;
  if (!hasTags) {
    debugLog('[probeTaskState] task has no tags', { taskId });
    return {
      ok: true,
      task,
      fullTags: null,
      viewerState: null,
      effectiveTagIds,
      hasTags: false,
      canVisualize: false,
    };
  }

  // A tag hydration failure is recoverable as a Task Board fallback.
  let fullTags;
  try {
    fullTags = await dispatch('GET_BASE_TAGS_BY_IDS', effectiveTagIds, { root: true });
  } catch (err) {
    debugError('[probeTaskState] GET_BASE_TAGS_BY_IDS failed', err);
    return {
      ok: true,
      task,
      fullTags: null,
      viewerState: null,
      effectiveTagIds,
      hasTags: true,
      canVisualize: false,
    };
  }
  if (!Array.isArray(fullTags) || fullTags.length === 0) {
    debugLog('[probeTaskState] tag fetch returned empty', { taskId });
    return {
      ok: true,
      task,
      fullTags,
      viewerState: null,
      effectiveTagIds,
      hasTags: true,
      canVisualize: false,
    };
  }

  const viewerState = await deriveViewerState(fullTags, rootState);
  const plan = viewerState.plan;
  const canVisualize = plan?.kind === 'viewer'
    && (plan.viewer === '3d'
      || (plan.viewer === '360' && !!viewerState.scan && !!viewerState.firstScanTag));

  debugLog('[probeTaskState]', {
    taskId,
    hasTags: true,
    canVisualize,
    viewer: viewerState.viewer,
    hasScan: !!viewerState.scan,
    planKind: plan?.kind,
    planReason: plan?.reason,
    currentAssetId: rootState.assetId,
  });

  return {
    ok: true,
    task,
    fullTags,
    viewerState,
    effectiveTagIds,
    hasTags: true,
    canVisualize,
  };
}
